import { type UserData } from '@beat-venues/common-types';
import { Header, LanguageSwitcher } from '@beat-venues/ui/react';
import { ReactElement, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Outlet,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';

import { UserContext } from '../../context/user-context';
import { logout, redirectAfterLogout } from '../../services';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { mapUserToAbbrev } from '../../utils/mapping-util';

const Root = (): ReactElement => {
    const { user } = useContext(UserContext);
    const { pathname } = useLocation();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const logoutParam = searchParams.get('logout');

    useEffect(() => {
        if (!sessionStorage.getItem('logout-info')) {
            sessionStorage.setItem('logout-info', document.referrer);
        }
        if (logoutParam === 'true') {
            console.log('performing logout');
            redirectAfterLogout(true);
        }
    }, []);

    useEffect(() => {
        if (user && (!user?.firstName || !user?.lastName)) {
            navigate(`/${t('routes.welcome') || 'welcome'}`);
            return;
        }
        if (user) {
            const redirectUrl = localStorage.getItem('redirect-after-login');
            if (redirectUrl) {
                localStorage.removeItem('redirect-after-login');
                window.location.href = redirectUrl;
            }
        }
    }, [user]);

    return (
        <>
            <Header
                accountLabel={t('userAccount.account') || ''}
                languageSwitcher={
                    <LanguageSwitcher
                        changeLanguage={i18n.changeLanguage}
                        currentLanguage={i18n.language}
                        supportedLanguages={SUPPORTED_LANGUAGES}
                    />
                }
                logout={logout}
                logoutLabel={t('userAccount.logout') || ''}
                navigationItems={[
                    {
                        id: '1',
                        label: `${t('navigation.events')}`,
                        href: `/${i18n.language}`,
                        isActive:
                            pathname === `/${i18n.language}` ||
                            pathname.includes(t('routes.events')),
                    },
                    {
                        id: '2',
                        label: `${t('navigation.preferences')}`,
                        href: `/${i18n.language}/${t('routes.preferences')}`,
                        isActive: pathname.includes(t('routes.preferences')),
                    },
                    {
                        id: '3',
                        label: `${t('navigation.account')}`,
                        href: `/${i18n.language}/${t('routes.account')}`,
                        isActive: pathname.includes(t('routes.account')),
                    },
                    {
                        id: '4',
                        label: `${t('navigation.presales')}`,
                        href: `/${i18n.language}/${t('routes.presales')}`,
                        isActive: pathname.includes(t('routes.presales')),
                    },
                ]}
                userAbbrev={mapUserToAbbrev(user as UserData)}
            />
            <Outlet />
        </>
    );
};

export { Root };
